import React from 'react'
import { ImageSourcePropType } from 'react-native'
import {
  ATLDialog,
  ATLDivider,
  ATLTextBody,
  ButtonTypeEnum,
  DialogTypeEnum,
  FontWeightEnum,
} from '@appsimples/atlantica-react-native'
import { atlTheme } from '../../../atlTheme'
import { formatMessage } from '../../../constants/strings/intl/intlHooks'
import { messages } from '../../../constants/strings/messages'
import { IconTwoText } from './IconTwoText'
import { EmptyCard } from './EmptyCard'
import styled from 'styled-components/native'
import { Coordinator } from '../../../navigation/coordinator/coordinator'
import { formatDateToBarcode, ifMoneyInt } from '../../../utils/formatters'
import { getInstallmentDateAndTypeLabel } from '../constants'
import { listUserSaleContracts_listUserSaleContracts } from '../../../services/types/listUserSaleContracts'
import { useLegacyState } from '../../../utils/hooks/useLegacyState'
import { GetStatement_listStatements_installmentsOverdue } from '../../../services/types/GetStatement'
import { MAX_BANK_SLIP } from '../../../constants/maxBankSlip'
import { getNewDateHourZero } from '../utils/getFineAndInterestValue'
import { Modal } from './Modal'
import { ModalWeb } from '../../../components/ModalWeb'

const GenericWhiteCard = styled.View`
  background-color: ${atlTheme.ColorSurface};
  border-radius: ${atlTheme.BorderRadiusCard}px;
  margin-vertical: ${atlTheme.SpacingAtomic}px;
`
export interface InstallmentsCardProps {
  data: any
  installmentType: string
  icon: ImageSourcePropType
  button?: boolean
  buttonTitle?: string
  buttonType?: ButtonTypeEnum
  currentSaleContract?: listUserSaleContracts_listUserSaleContracts
  getDuplicateBankSlip?: (installment: any) => void
  secondBankSlipRegistered?: GetStatement_listStatements_installmentsOverdue[]
  isInstallmentsOverdue?: boolean
}
export const InstallmentsCard: React.FunctionComponent<InstallmentsCardProps> = ({
  data,
  installmentType,
  buttonType,
  buttonTitle,
  button,
  icon,
  getDuplicateBankSlip,
  secondBankSlipRegistered,
  isInstallmentsOverdue,
}) => {
  const [state, setState] = useLegacyState({
    isDialogUnavailableDuplicateBank: false,
    isDialogWarningInterest: false,
    installmentSelected: undefined as any,
  })
  const { isDialogUnavailableDuplicateBank, isDialogWarningInterest } = state

  const onPressPayInstallmentOverdue = installment => {
    const expiredBillet = verifyExpiredBillet(installment?.digitableNumber)
    if (installment?.digitableNumber && !expiredBillet) {
      Coordinator.goToPayInstallment(installment)
    } else {
      handlePermissionGetDuplicateBank(installment)
    }
  }

  const onPressPayInstallmentOpen = installment => {
    if (installment?.digitableNumber) {
      Coordinator.goToPayInstallment(installment)
    } else {
      handlePermissionGetDuplicateBank(installment)
    }
  }

  const onPressPayInstallment = isInstallmentsOverdue ? onPressPayInstallmentOverdue : onPressPayInstallmentOpen

  const openDialogUnavailableDuplicateBank = () => {
    setState({ isDialogUnavailableDuplicateBank: true })
  }
  const openDialogDialogWarningInterest = () => {
    setState({ isDialogWarningInterest: true })
  }

  const closeDialogDialogWarningInterest = () => {
    setState({ isDialogWarningInterest: false })
  }
  const closeDialogUnavailableDuplicateBank = () => {
    setState({ isDialogUnavailableDuplicateBank: false })
  }

  const onPressConfirmDialogUnavailableDuplicateBank = () => {
    closeDialogUnavailableDuplicateBank()
    Coordinator.goToDuplicateScreen()
  }

  const onPressConfirmDialogDialogWarningInterest = () => {
    const installment = state.installmentSelected
    getDuplicateBankSlip(installment)
    closeDialogDialogWarningInterest()
  }

  const verifyExpiredBillet = (barcode?: string) => {
    if (barcode) {
      const dateBarCode = formatDateToBarcode(barcode)
      return dateBarCode < getNewDateHourZero()
    }
    return true
  }

  const handlePermissionGetDuplicateBank = installmentSelected => {
    const bankSlip = findSecondBankSlipRegistered(installmentSelected)

    if (bankSlip?.urlReport) return Coordinator.goToPayInstallment(bankSlip)
    if (secondBankSlipRegistered?.length >= MAX_BANK_SLIP) return openDialogUnavailableDuplicateBank()

    setState({ installmentSelected })
    openDialogDialogWarningInterest()
  }

  const findSecondBankSlipRegistered = installmentSelected => {
    return secondBankSlipRegistered?.find(bankSlip => bankSlip?.installmentId === installmentSelected?.installmentId)
  }

  return data?.length ? (
    <GenericWhiteCard>
      <Modal
        onPressDismiss={closeDialogUnavailableDuplicateBank}
        type={DialogTypeEnum.alert}
        open={isDialogUnavailableDuplicateBank}
        title={formatMessage(messages.financeBillsScreen_dialog_title)}
        description={formatMessage(messages.financeBillsScreen_dialog_description)}
        buttonTexts={[
          formatMessage(messages.financeScreen_dialogButton_cancel),
          formatMessage(messages.financeScreen_dialogButton_request),
        ]}
        buttonBaseProps={{
          textColorComplementary: atlTheme.ColorBrand2High,
          fontWeight: FontWeightEnum.medium,
          fontSize: 12,
        }}
        onPressConfirm={onPressConfirmDialogUnavailableDuplicateBank}
        titleLabelBaseProps={{ fontWeight: FontWeightEnum.medium }}
        onClose={closeDialogUnavailableDuplicateBank}
        onCancelButton={closeDialogUnavailableDuplicateBank}
        onConfirmButton={onPressConfirmDialogUnavailableDuplicateBank}
        messageCancelButton={messages.financeScreen_dialogButton_cancel}
        messageConfirmButton={messages.financeScreen_dialogButton_request}
      />
      <Modal
        onPressDismiss={closeDialogDialogWarningInterest}
        type={DialogTypeEnum.alert}
        open={isDialogWarningInterest}
        title={formatMessage(messages.financeBillsScreen_dialog_warning_interest_title)}
        description={formatMessage(messages.financeBillsScreen_dialog_warning_interest_description)}
        buttonTexts={[
          formatMessage(messages.financeScreen_dialogButton_cancel),
          formatMessage(messages.financeScreen_dialogButton_request),
        ]}
        buttonBaseProps={{
          textColorComplementary: atlTheme.ColorBrand2High,
          fontWeight: FontWeightEnum.medium,
          fontSize: 12,
        }}
        onPressConfirm={onPressConfirmDialogDialogWarningInterest}
        titleLabelBaseProps={{ fontWeight: FontWeightEnum.medium }}
        onClose={closeDialogDialogWarningInterest}
        onCancelButton={closeDialogDialogWarningInterest}
        onConfirmButton={onPressConfirmDialogDialogWarningInterest}
        messageCancelButton={messages.financeScreen_dialogButton_cancel}
        messageConfirmButton={messages.financeScreen_dialogButton_request}
      />
      <ATLTextBody
        labelBaseProps={{
          multilineLimit: 1,
          fontSize: atlTheme.FontSizeHeading,
          lineHeight: atlTheme.FontSizeDisplay,
          fontWeight: atlTheme.FontWeight500,
        }}
      >
        {`${formatMessage(messages.financeScreen_installments)} ${installmentType}`}
      </ATLTextBody>
      {data.map((installment, index) => {
        const date = new Date(installment.dueDate)
        const isNotFirst = index !== 0
        return (
          <>
            {isNotFirst && <ATLDivider dividerBaseProps={{ color: atlTheme.ColorBackground }} />}
            <IconTwoText
              key={index}
              icon={icon}
              button={button}
              buttonType={buttonType}
              buttonTitle={buttonTitle}
              firstTextVertical={getInstallmentDateAndTypeLabel(date, installment?.conditionDescription)}
              secondTextVertical={`${ifMoneyInt(installment.currentBalance)}`}
              onPress={() => onPressPayInstallment(installment)}
              twoText
            />
          </>
        )
      })}
    </GenericWhiteCard>
  ) : (
    <EmptyCard type={installmentType} />
  )
}
